import { rgApi } from "@/lib/rgApi";
import { MessageCircle } from "lucide-react";
import FormBlock from "./FormBlock";

/**
 * BreakfastBlock — suivi du petit-déjeuner, présenté via le bloc harmonisé FormBlock.
 * Le lien WhatsApp passe par le wrapper d'aperçu /api/rg/pd/{token} (photo de petit-déj
 * + titre « Votre petit-déjeuner au Curieux Refuge ») et redirige vers le formulaire
 * sécurisé /petit-dejeuner/{token}. Les réponses se rattachent à cette réservation.
 */

const FALLBACK_TPL =
  "Bonjour {first_name} 🥐\nPour préparer au mieux votre petit déjeuner, pouvez-vous remplir ce petit formulaire dès que vous avez un moment ?\n{lien}\nMerci beaucoup et à très vite ✨";

// Numéro du collègue (récap petit-déjeuner) — envoi manuel, aucune API.
const COLLEAGUE_WA = "262692340010";

const FR_WEEKDAY_DATE = (iso) =>
  iso ? new Date(iso + "T12:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }) : "";

export default function BreakfastBlock({ reservation: r, templates = {}, onLog, onPatch, busy }) {
  const fs = r.form_status || {};
  const bf = r.breakfast_form || {};
  const received = bf.received === true || fs.petit_dejeuner === "received";
  const state = received ? "received" : fs.petit_dejeuner === "sent" ? "sent" : "to_send";

  const firstName = (r.name || "").trim().split(/\s+/)[0] || "";
  const effectivePeople = r.extra_guest_added && (r.people || 0) < 3 ? (r.people || 0) + 1 : (r.people || 0);

  // Récap complet du petit-déjeuner pour le collègue (envoi WhatsApp MANUEL).
  const buildColleagueRecap = () => {
    const L = [];
    L.push("🥐 Petit-déjeuner — Curieux Refuge");
    L.push(`Voyageur : ${r.name || "—"}`);
    L.push(`Séjour : ${FR_WEEKDAY_DATE(r.date_arrival)} → ${FR_WEEKDAY_DATE(r.date_departure)}`);
    if (bf.date) L.push(`Petit-déjeuner : ${FR_WEEKDAY_DATE(bf.date)}${bf.time ? ` à ${bf.time}` : ""}`);
    L.push(`Personnes : ${effectivePeople || "—"}`);
    L.push("");
    L.push("Choix :");
    (bf.voyageurs || []).forEach((v, i) => {
      L.push(`• Voyageur ${i + 1} : ${v.protein || "—"} · ${v.boisson || "—"}`);
    });
    if (bf.beurre) L.push(`• Beurre : ${bf.beurre}`);
    if (Array.isArray(bf.confitures) && bf.confitures.length) L.push(`• Confitures : ${bf.confitures.join(" · ")}`);
    if (bf.comments) L.push(`• Remarque : ${bf.comments}`);
    return L.join("\n");
  };

  const sendColleagueRecap = () => {
    const msg = buildColleagueRecap();
    window.open(`https://wa.me/${COLLEAGUE_WA}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
  };

  const buildMessage = (link) => {
    const raw = templates.form_petit_dejeuner || FALLBACK_TPL;
    return raw.replace(/\{first_name\}/g, firstName).replace(/\{lien\}/g, link).trim();
  };

  const setStatus = (value) => {
    const next = { ...fs };
    if (value === null) delete next.petit_dejeuner;
    else next.petit_dejeuner = value;
    onPatch({ form_status: next });
  };

  const receivedNode = (
    <div className="rg-card" data-testid="rg-bf-answers" style={{ padding: 12, background: "var(--rg-bg)" }}>
      <p className="rg-tiny" style={{ margin: "0 0 8px", fontWeight: 600, color: "#2f7a43" }}>Réponses reçues</p>
      <div style={{ fontSize: 13, lineHeight: 1.6 }}>
        {bf.date && <div><b>Date :</b> {new Date(bf.date + "T12:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</div>}
        {bf.time && <div><b>Heure :</b> {bf.time}</div>}
        {(bf.voyageurs || []).map((v, i) => (
          <div key={i}>Voyageur {i + 1} : {v.protein || "—"} · {v.boisson || "—"}</div>
        ))}
        {bf.beurre && <div><b>Beurre :</b> 🧈 {bf.beurre}</div>}
        {Array.isArray(bf.confitures) && bf.confitures.length > 0 && (
          <div><b>Confitures :</b> {bf.confitures.join(" · ")}</div>
        )}
        {bf.comments && <div style={{ marginTop: 4, fontStyle: "italic" }}>📝 {bf.comments}</div>}
      </div>
      <button type="button" className="rg-btn small" onClick={sendColleagueRecap}
        data-testid="rg-bf-send-colleague"
        style={{ marginTop: 12, background: "#25D366", borderColor: "#25D366", color: "#fff" }}>
        <MessageCircle size={13} /> Envoyer le récap au collègue
      </button>
      <p className="rg-tiny" style={{ margin: "6px 0 0", color: "var(--rg-text-3)" }}>
        Ouvre WhatsApp au +262 692 34 00 10 avec le récap prérempli — tu vérifies puis tu appuies sur Envoyer.
      </p>
    </div>
  );

  return (
    <FormBlock
      icon="🥐"
      title="Petit déjeuner"
      subtitle="Envoie le lien individuel du formulaire (tu gardes la main). Les réponses se rattachent automatiquement à cette réservation."
      anchorId="petit-dejeuner"
      testid="rg-bf"
      state={state}
      phone={r.phone}
      busy={busy}
      fetchToken={() => rgApi.getBreakfastLink(r.id).then((res) => res.token)}
      wrapperPath="pd"
      previewPath="petit-dejeuner"
      buildMessage={buildMessage}
      onSent={(channel, msg) => { if (onLog) onLog(channel, msg); if (!received) setStatus("sent"); }}
      onCancel={() => setStatus(null)}
      onReopen={() => setStatus("sent")}
      receivedNode={receivedNode}
    />
  );
}
